import Box from "@mui/material/Box";
import { useState } from "react";

import { useSelector, useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import { actionMapMapviewChangeRequested } from "../Map/MapRedux";
import { SCALE_ZOOM_THRESHOLD } from "../ScaleGrid";
import ViewAwareSlider from "../Shared/ViewAwareSlider";

let rotateMarks = [];
for (let i = -180; i < 200; i += 45) {
  rotateMarks.push({ value: i, label: i + "°" });
}

let pitchMarks = [];
for (let j = 0; j < 65; j += 10) {
  pitchMarks.push({ value: j, label: j + "°" });
}

const root_ = (theme) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  pointerEvents: "none",
  textAlign: "center",
});

const rotate_ = (theme) => ({
  textAlign: "center",
  position: "fixed",
  bottom: 6,
  minWidth: 600,
  width: "50%",
  left: "50%",
  transform: "translate(-50%, 0)",
  margin: "0px 10px",
  padding: "0px 5px",
  pointerEvents: "auto",
  backgroundColor: "rgba(255, 255, 255, 1)",
  boxShadow:
    "0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)",
});

const sliderTrack_ = (theme) => ({
  width: "0 !important",
});

const pitch_ = (theme) => ({
  position: "fixed",
  right: 100,
  top: "50%",
  transform: "translate(0, -50%)",
  height: "80vh",
  width: 100,
  textAlign: "center",
  pointerEvents: "auto",
  backgroundColor: "rgba(255, 255, 255, 1)",
  // padding: '5px 0',
  boxShadow:
    "0px 1px 3px 0px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 2px 1px -1px rgba(0,0,0,0.12)",
});

const sliderVertical_ = (theme) => ({
  marginRight: "0 !important",
  height: "92% !important",
});

const grid_ = (theme) => ({
  width: "69vmin",
  height: "69vmin",
  display: "inline-block",
  position: "relative",
  margin: "10px",
  verticalAlign: "top",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to right, #4455b5 1px, transparent 1px)",
    backgroundSize: "10%",
    // specific
    backgroundColor: "transparent",
    boxShadow: "inset 0px 0px 0 1px #4455b5",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    background: "linear-gradient(to right, #4455b5 1px, transparent 1px)",
    backgroundSize: "10%",
    // specific
    transform: "rotate(90deg)",
  },
});

export default function Compass({ visible }) {
  const dispatch = useDispatch();

  // handle interaction with 1'x1' scale grid
  const zoom = useSelector((state) => state.mapReducer.zoom);
  const scaleGridVisibleRedux = useSelector(
    (state) => state.scaleReducer.gridVisible,
  );
  const scaleGridVisible =
    zoom >= SCALE_ZOOM_THRESHOLD && scaleGridVisibleRedux;

  const bearing = useSelector((state) => state.mapReducer.bearing);
  const pitch = useSelector((state) => state.mapReducer.pitch);

  const [gridVisible, setGridVisible] = useState(false);
  const mouseEnterHandler = () => {
    setGridVisible(true);
  };
  const mouseExitHandler = () => {
    setGridVisible(false);
  };

  const handlePitchValue = (event, val) => {
    dispatch(
      actionMapMapviewChangeRequested({
        pitch: val,
        fromSlider: true,
      }),
    );
  };

  const resetPitch = () => {
    dispatch(
      actionMapMapviewChangeRequested({
        pitch: 0,
      }),
    );
  };

  const handleRotateValue = (event, val) => {
    dispatch(
      actionMapMapviewChangeRequested({
        bearing: val,
        fromSlider: true,
      }),
    );
  };

  const resetBearing = () => {
    dispatch(
      actionMapMapviewChangeRequested({
        bearing: 0,
      }),
    );
  };

  return (
    <>
      <Box
        sx={root_}
        style={{
          display: visible && !scaleGridVisible ? "block" : "none",
          opacity: gridVisible && !scaleGridVisible ? 1 : 0.9,
        }}
      >
        <Box sx={grid_} style={{ opacity: gridVisible ? 1 : 0.5 }}></Box>
      </Box>
      <Box
        style={{ display: visible ? "block" : "none" }}
        sx={rotate_}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseExitHandler}
      >
        <Tooltip title="Reset Rotation">
          <Button color="primary" onClick={resetBearing} role="region">
            Rotation:{bearing.toFixed(1)}&#176;
          </Button>
        </Tooltip>
        <ViewAwareSlider
          value={bearing}
          max={180}
          min={-180}
          marks={rotateMarks}
          onChange={handleRotateValue}
          slotProps={{
            track: {
              sx: sliderTrack_,
            },
          }}
          data-testid="rotate-slider"
          size="small"
          label="Rotate"
        />
      </Box>
      <Box
        style={{ display: visible ? "block" : "none" }}
        sx={pitch_}
        onMouseEnter={mouseEnterHandler}
        onMouseLeave={mouseExitHandler}
      >
        <Tooltip title="Reset Pitch">
          <Button color="primary" onClick={resetPitch} role="region">
            Pitch:{pitch.toFixed()}&#176;
          </Button>
        </Tooltip>
        <ViewAwareSlider
          orientation="vertical"
          value={pitch}
          min={0}
          max={60}
          marks={pitchMarks}
          onChange={handlePitchValue}
          sx={sliderVertical_}
          slotProps={{
            track: {
              sx: sliderTrack_,
            },
          }}
          data-testid="pitch-slider"
          size="small"
          label="Pitch"
        />
      </Box>
    </>
  );
}
